import { useEffect, useState } from "react"
import { api, ApiError } from "../../lib/api"
import { useSelectedAccount } from "../../contexts/AccountContext"
import InviteModal from "./modals/InviteModal"
import ChangeRoleModal from "./modals/ChangeRoleModal"

export type Member = {
  id: number
  user_id: number
  name: string
  email: string
  avatar_url: string | null
  role: "member" | "owner"
  joined_at: string
}

const ROLE_LABELS: Record<Member["role"], string> = {
  member: "メンバー",
  owner: "オーナー",
}

const MembersTab = () => {
  const { selectedOrganizationId } = useSelectedAccount()
  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [changeRoleTarget, setChangeRoleTarget] = useState<Member | null>(null)

  useEffect(() => {
    setLoading(true)
    const params = selectedOrganizationId !== null ? `?organization_id=${selectedOrganizationId}` : ""
    api.get<Member[]>(`/organization_members${params}`)
      .then(data => {
        setMembers(data)
        setLoading(false)
      })
      .catch(() => {
        setError("メンバー情報の取得に失敗しました")
        setLoading(false)
      })
  }, [selectedOrganizationId])

  const handleDelete = async (member: Member) => {
    setError(null)
    setSuccessMessage(null)
    try {
      const params = selectedOrganizationId !== null ? `?organization_id=${selectedOrganizationId}` : ""
      await api.delete(`/organization_members/${member.id}${params}`)
      setSuccessMessage(`${member.name} を削除しました`)
      setMembers(prev => prev.filter(m => m.id !== member.id))
      setChangeRoleTarget(null)
    } catch (err) {
      const { data: errData } = err as ApiError
      setError(errData.error ?? "削除に失敗しました")
    }
  }

  const handleRoleChanged = (updated: Member) => {
    setMembers(prev => prev.map(m => m.id === updated.id ? updated : m))
    setSuccessMessage(`${updated.name} のロールを変更しました`)
    setChangeRoleTarget(null)
  }

  return (
    <div className="settings-card">
      <div className="settings-card__header">
        <i className="bi bi-people" />
        <h2>メンバー一覧</h2>
        <button className="btn-invite" onClick={() => setShowInviteModal(true)}>
          <i className="bi bi-person-plus-fill" />
          メンバーを招待
        </button>
      </div>

      <div className="settings-card__body settings-card__body--flush">
        {(error || successMessage) && (
          <div className="members-alerts">
            {error && <div className="settings-alert settings-alert--error">{error}</div>}
            {successMessage && <div className="settings-alert settings-alert--success">{successMessage}</div>}
          </div>
        )}

        {loading ? (
          <p className="members-loading">読み込み中...</p>
        ) : (
          <div className="members-table-wrapper">
            <table className="members-table">
              <thead>
                <tr>
                  <th>メンバー</th>
                  <th>メールアドレス</th>
                  <th>ロール</th>
                  <th>参加日</th>
                  <th className="members-table__actions-col">操作</th>
                </tr>
              </thead>
              <tbody>
                {members.map(member => (
                  <tr key={member.id}>
                    <td>
                      <div className="member-name-cell">
                        <div className="member-avatar">
                          {member.avatar_url
                            ? <img src={member.avatar_url} alt={member.name} />
                            : <span>{member.name[0]}</span>
                          }
                        </div>
                        <span className="member-name-cell__name">
                          {member.name}
                        </span>
                      </div>
                    </td>
                    <td className="members-table__email">{member.email}</td>
                    <td>
                      <span className={`member-badge member-badge--${member.role}`}>
                        {ROLE_LABELS[member.role]}
                      </span>
                    </td>
                    <td className="members-table__date">{member.joined_at}</td>
                    <td className="members-table__actions">
                      <button
                        className="btn-icon"
                        title="編集"
                        onClick={() => setChangeRoleTarget(member)}
                      >
                        <i className="bi bi-pencil" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showInviteModal && (
        <InviteModal
          onClose={() => setShowInviteModal(false)}
          onInvited={(message) => {
            setShowInviteModal(false)
            setSuccessMessage(message)
          }}
        />
      )}

      {changeRoleTarget && (
        <ChangeRoleModal
          member={changeRoleTarget}
          onClose={() => setChangeRoleTarget(null)}
          onChanged={handleRoleChanged}
          onDeleted={handleDelete}
        />
      )}
    </div>
  )
}

export default MembersTab
